import moment from "../../../../../utils/app_moment";

interface HeldAppointmentBooking {
    id: string;
    starting_date?: string | null;
    supervisor_intake_session?: {
        start_time?: string | null;
    } | null;
}

interface HeldAppointmentClient {
    held_bookings?: HeldAppointmentBooking[] | null;
}

export interface HeldAppointmentStartDate {
    id: string;
    date: string;
    timestamp: number;
}

function getValidTimestamp(date?: string | null): number | null {
    if (!date) {
        return null;
    }

    const parsedDate = moment(date, moment.ISO_8601, true);
    return parsedDate.isValid() ? parsedDate.valueOf() : null;
}

function getHeldAppointmentStartDate(
    booking: HeldAppointmentBooking
): HeldAppointmentStartDate | null {
    const supervisorDate = booking.supervisor_intake_session?.start_time;
    const supervisorTimestamp = getValidTimestamp(supervisorDate);

    if (supervisorTimestamp !== null && supervisorDate) {
        return {
            id: booking.id,
            date: supervisorDate,
            timestamp: supervisorTimestamp,
        };
    }

    const bookingTimestamp = getValidTimestamp(booking.starting_date);
    return bookingTimestamp !== null && booking.starting_date
        ? {
              id: booking.id,
              date: booking.starting_date,
              timestamp: bookingTimestamp,
          }
        : null;
}

export function getHeldAppointmentStartDates(
    client: HeldAppointmentClient
): HeldAppointmentStartDate[] {
    return (client.held_bookings ?? [])
        .map(getHeldAppointmentStartDate)
        .filter(
            (appointment): appointment is HeldAppointmentStartDate =>
                appointment !== null
        )
        .sort((a, b) => a.timestamp - b.timestamp);
}

function getEarliestHeldAppointmentStartTimestamp(
    client: HeldAppointmentClient
): number | null {
    return (client.held_bookings ?? []).reduce<number | null>(
        (earliestTimestamp, booking) => {
            const effectiveTimestamp =
                getHeldAppointmentStartDate(booking)?.timestamp;

            if (effectiveTimestamp === undefined) {
                return earliestTimestamp;
            }

            return earliestTimestamp === null
                ? effectiveTimestamp
                : Math.min(earliestTimestamp, effectiveTimestamp);
        },
        null
    );
}

export function compareClientsByHeldAppointmentStartDate(
    firstClient: HeldAppointmentClient,
    secondClient: HeldAppointmentClient
): number {
    const firstTimestamp = getEarliestHeldAppointmentStartTimestamp(firstClient);
    const secondTimestamp = getEarliestHeldAppointmentStartTimestamp(secondClient);

    if (firstTimestamp === null) {
        return secondTimestamp === null ? 0 : 1;
    }

    if (secondTimestamp === null) {
        return -1;
    }

    return firstTimestamp - secondTimestamp;
}
